import { ComponentPropsWithRef, ReactNode, useId } from 'react';
import { LuCheck, LuChevronDown, LuChevronRight, LuX } from 'react-icons/lu';

import { cn } from '@/utils/cn';

import type { Autosave } from './useAutosave';

export const inputClass = (invalid?: boolean) =>
    cn(
        'h-9 w-full rounded border bg-white px-2.5 text-sm text-cocoa-900 disabled:cursor-not-allowed disabled:bg-sand-100 disabled:text-cocoa-500',
        invalid ? 'border-gold-500' : 'border-border',
    );

export const linkButtonClass =
    'text-gold-600 hover:text-gold-500 cursor-pointer text-[13px] font-medium underline underline-offset-[3px] disabled:cursor-not-allowed disabled:opacity-50';

/** A label, the control, and the "Required" / helper line under it. */
export const Field = ({
    label,
    missing,
    error,
    helper,
    className,
    children,
}: {
    label?: string;
    missing?: boolean;
    error?: string;
    helper?: ReactNode;
    className?: string;
    children: (id: string) => ReactNode;
}) => {
    const id = useId();
    return (
        <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
            {label && (
                <label htmlFor={id} className="text-cocoa-600 text-xs font-medium">
                    {label}
                </label>
            )}
            {children(id)}
            {missing && <span className="text-gold-600 text-xs font-medium">Required</span>}
            {error && <span className="text-gold-600 text-xs">{error}</span>}
            {helper && <span className="text-cocoa-400 text-xs">{helper}</span>}
        </div>
    );
};

export const TextField = ({
    label,
    value,
    onChange,
    required,
    placeholder,
    disabled,
    helper,
    error,
    className,
}: {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    required?: boolean;
    placeholder?: string;
    disabled?: boolean;
    helper?: ReactNode;
    error?: string;
    className?: string;
}) => {
    const missing = !!required && !value.trim();
    return (
        <Field label={label} missing={missing} error={error} helper={helper} className={className}>
            {(id) => (
                <input
                    id={id}
                    className={inputClass(missing || !!error)}
                    value={value}
                    placeholder={placeholder}
                    disabled={disabled}
                    onChange={(event) => onChange(event.target.value)}
                />
            )}
        </Field>
    );
};

/** A date with the browser's own date picker; the value is the stored `YYYY-MM-DD` form, empty while unset or incomplete. */
export const DateField = ({
    label,
    value,
    onChange,
    required,
    disabled,
    max,
}: {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    required?: boolean;
    disabled?: boolean;
    /** The latest date the picker offers, `YYYY-MM-DD`. */
    max?: string;
}) => {
    const missing = !!required && !value;
    return (
        <Field label={label} missing={missing}>
            {(id) => (
                <input
                    id={id}
                    type="date"
                    className={inputClass(missing)}
                    value={value}
                    max={max}
                    disabled={disabled}
                    onChange={(event) => onChange(event.target.value)}
                />
            )}
        </Field>
    );
};

export interface Option {
    value: string;
    label: string;
    disabled?: boolean;
}

/**
 * A native select with its own chevron: the browser's arrow sits against the right border, this one is inset like the
 * text. `fit` sizes it to its content instead of the full column.
 */
export const NativeSelect = ({
    id,
    value,
    onChange,
    options,
    placeholder,
    disabled,
    invalid,
    fit,
    className,
    'aria-label': ariaLabel,
}: {
    id?: string;
    value: string;
    onChange: (value: string) => void;
    options: Option[];
    placeholder?: string;
    disabled?: boolean;
    invalid?: boolean;
    fit?: boolean;
    className?: string;
    'aria-label'?: string;
}) => (
    <span className={cn('relative inline-flex', fit ? 'w-auto' : 'w-full')}>
        <select
            id={id}
            aria-label={ariaLabel}
            className={cn(inputClass(invalid), 'appearance-none pr-8', className)}
            value={value}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
        >
            {placeholder !== undefined && <option value="">{placeholder}</option>}
            {options.map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                </option>
            ))}
        </select>
        <LuChevronDown
            aria-hidden
            className="text-cocoa-500 pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2"
        />
    </span>
);

export const SelectField = ({
    label,
    value,
    onChange,
    options,
    required,
    placeholder = 'Select…',
    disabled,
    helper,
    className,
}: {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    options: Option[];
    required?: boolean;
    placeholder?: string;
    disabled?: boolean;
    helper?: ReactNode;
    className?: string;
}) => {
    const missing = !!required && !value;
    return (
        <Field label={label} missing={missing} helper={helper} className={className}>
            {(id) => (
                <NativeSelect
                    id={id}
                    value={value}
                    onChange={onChange}
                    options={options}
                    placeholder={placeholder}
                    disabled={disabled}
                    invalid={missing}
                />
            )}
        </Field>
    );
};

const segmentClass = (on: boolean, position: 'first' | 'middle' | 'last') =>
    cn(
        'relative -ml-px h-8 cursor-pointer border px-3.5 text-[13px] first:ml-0 disabled:cursor-not-allowed disabled:opacity-60',
        on ? 'border-cocoa-900 bg-cocoa-900 z-10 text-white' : 'border-border text-cocoa-900 bg-white',
        position === 'first' && 'rounded-l',
        position === 'last' && 'rounded-r',
    );

/** Adjacent toggle buttons, one of which is on. */
export const Segmented = <T extends string>({
    value,
    onChange,
    options,
    disabled,
    'aria-label': ariaLabel,
}: {
    value?: T;
    onChange: (value: T) => void;
    options: readonly T[] | { value: T; label: string }[];
    disabled?: boolean;
    'aria-label'?: string;
}) => {
    const items = options.map((option) => (typeof option === 'string' ? { value: option, label: option } : option));
    return (
        <div role="radiogroup" aria-label={ariaLabel} className="flex">
            {items.map((item, index) => (
                <button
                    key={item.value}
                    type="button"
                    role="radio"
                    aria-checked={value === item.value}
                    disabled={disabled}
                    className={segmentClass(
                        value === item.value,
                        index === 0 ? 'first' : index === items.length - 1 ? 'last' : 'middle',
                    )}
                    onClick={() => onChange(item.value)}
                >
                    {item.label}
                </button>
            ))}
        </div>
    );
};

/** A yes/no answer; unanswered (`undefined`) shows neither on and counts as missing when required. */
export const YesNoField = ({
    label,
    value,
    onChange,
    required,
    disabled,
    helper,
}: {
    label: string;
    value?: boolean;
    onChange: (value: boolean) => void;
    required?: boolean;
    disabled?: boolean;
    helper?: ReactNode;
}) => (
    <Field label={label} missing={!!required && value === undefined} helper={helper}>
        {() => (
            <Segmented
                aria-label={label}
                value={value === undefined ? undefined : value ? 'Yes' : 'No'}
                onChange={(answer) => onChange(answer === 'Yes')}
                options={['Yes', 'No'] as const}
                disabled={disabled}
            />
        )}
    </Field>
);

export const CheckBox = ({
    checked,
    onChange,
    label,
    disabled,
}: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label?: string;
    disabled?: boolean;
}) => (
    <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={label ? undefined : 'Toggle'}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className="text-cocoa-900 flex min-h-9 cursor-pointer items-center gap-2.5 text-left text-sm disabled:cursor-not-allowed disabled:opacity-60"
    >
        <span
            className={cn(
                'inline-flex size-[18px] flex-none items-center justify-center rounded border-[1.5px]',
                checked ? 'border-cocoa-900 bg-cocoa-900' : 'border-cocoa-300 bg-white',
            )}
        >
            {checked && <LuCheck className="size-3 text-white" strokeWidth={3} />}
        </span>
        {label}
    </button>
);

export const ReadField = ({ label, value }: { label: string; value?: ReactNode }) => (
    <Field label={label}>{() => <span className="flex min-h-9 items-center text-sm">{value || '—'}</span>}</Field>
);

export const RemoveButton = ({ onClick, label = 'Remove' }: { onClick: () => void; label?: string }) => (
    <button
        type="button"
        aria-label={label}
        onClick={onClick}
        className="text-cocoa-400 hover:text-cocoa-700 flex cursor-pointer"
    >
        <LuX className="size-3.5" />
    </button>
);

/** A small-caps heading of a group of fields inside a card, with an optional action beside it. */
export const GroupTitle = ({ children, action }: { children: ReactNode; action?: ReactNode }) => (
    <div className="flex items-center gap-4">
        <span className="text-cocoa-400 text-xs font-medium tracking-[0.1em] uppercase">{children}</span>
        {action}
    </div>
);

export const FieldGroup = ({
    title,
    action,
    columns,
    children,
}: {
    title?: ReactNode;
    action?: ReactNode;
    /** A fixed column count; otherwise the fields flow at ~200px each. */
    columns?: 2 | 3 | 4;
    children: ReactNode;
}) => (
    <div className="border-cocoa-100 flex flex-col gap-3 border-b py-[18px]">
        {title && <GroupTitle action={action}>{title}</GroupTitle>}
        <div
            className={cn(
                'grid items-start gap-x-5 gap-y-4',
                columns === 2 && 'grid-cols-2',
                columns === 3 && 'grid-cols-3',
                columns === 4 && 'grid-cols-4',
                !columns && 'grid-cols-[repeat(auto-fill,minmax(min(100%,200px),1fr))]',
            )}
        >
            {children}
        </div>
    </div>
);

/** Shown on parts of a card whose values the backend cannot store yet. */
export const NotSavedNote = ({ children = 'Not saved yet: needs backend support.' }: { children?: ReactNode }) => (
    <span className="bg-sand-100 text-cocoa-600 rounded px-2 py-0.5 text-xs">{children}</span>
);

export const StatusText = ({ complete, label }: { complete: boolean; label: string }) => (
    <span
        className={cn(
            'inline-flex min-w-[90px] flex-none items-center justify-end gap-1.5 text-[13px] font-medium whitespace-nowrap',
            complete ? 'text-cocoa-500' : 'text-gold-600',
        )}
    >
        {complete && <LuCheck className="text-seafoam-600 size-[13px]" strokeWidth={2.4} />}
        {label}
    </span>
);

export const Chevron = ({ open }: { open: boolean }) => (
    <LuChevronRight
        className={cn('text-cocoa-400 size-3.5 flex-none transition-transform duration-200', open && 'rotate-90')}
    />
);

/** A card with a clickable header row that opens its body. */
export const CollapsibleCard = ({
    id,
    open,
    onToggle,
    header,
    bodyProps,
    children,
}: {
    id?: string;
    open: boolean;
    onToggle: () => void;
    header: ReactNode;
    /** For the open body, e.g. the focus handlers of autosave. */
    bodyProps?: ComponentPropsWithRef<'div'>;
    children: ReactNode;
}) => (
    // Clipped rather than hidden overflow, so a floating footer can stick inside the card
    <div id={id} className="border-border overflow-clip rounded-lg border bg-white">
        <div
            role="button"
            tabIndex={0}
            aria-expanded={open}
            onClick={onToggle}
            onKeyDown={(event) => {
                if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    onToggle();
                }
            }}
            className="hover:bg-sand-100 flex cursor-pointer items-center gap-4 px-5 py-3.5 transition-colors"
        >
            <Chevron open={open} />
            {header}
        </div>
        {open && (
            <div {...bodyProps} className="border-cocoa-100 border-t px-5">
                {children}
            </div>
        )}
    </div>
);

/** A section heading in the serif face, with an optional action at the right. */
export const SectionHeading = ({
    children,
    action,
    open,
    onToggle,
}: {
    children: ReactNode;
    action?: ReactNode;
    open?: boolean;
    onToggle?: () => void;
}) => (
    <div className="flex items-center justify-between gap-3">
        {onToggle ? (
            <button
                type="button"
                aria-expanded={open}
                onClick={onToggle}
                className="text-cocoa-900 flex cursor-pointer items-center gap-2.5"
            >
                <Chevron open={!!open} />
                <h2 className="font-heading text-[22px] font-normal">{children}</h2>
            </button>
        ) : (
            <h2 className="font-heading text-[22px] font-normal">{children}</h2>
        )}
        {action}
    </div>
);

/** The header mark of a card whose changes did not save, seen while it is collapsed too. */
export const NotSavedMark = () => (
    <span className="text-gold-600 text-[13px] font-medium whitespace-nowrap">Not saved</span>
);

/** Whether a card holds changes that did not save, which the user has to act on. */
export const needsAttention = ({ status }: Autosave) => status === 'blocked' || status === 'failed';

/** Where a card's automatic saving stands, in its footer. */
export const AutosaveNote = ({ autosave }: { autosave: Autosave }) => (
    <span
        role="status"
        className={cn(
            'inline-flex items-center gap-1.5 text-[13px]',
            needsAttention(autosave) ? 'text-gold-600 font-medium' : 'text-cocoa-500',
        )}
    >
        {autosave.status === 'saving' && 'Saving…'}
        {autosave.status === 'saved' && (
            <>
                <LuCheck className="text-seafoam-600 size-[13px]" strokeWidth={2.4} />
                All changes saved
            </>
        )}
        {autosave.status === 'blocked' && <>Not saved: {autosave.blocker}</>}
        {autosave.status === 'failed' && (
            <>
                Couldn’t save.
                <button type="button" className={linkButtonClass} onClick={autosave.flush}>
                    Try again
                </button>
            </>
        )}
        {(autosave.status === 'idle' || autosave.status === 'editing') && 'Changes save automatically'}
    </span>
);

/**
 * The row under a card. `floating` keeps it at the bottom of the screen while the card is in view, never beyond the
 * card: for changes that did not save in a form longer than the screen.
 */
export const CardFooter = ({ children, floating }: { children: ReactNode; floating?: boolean }) => (
    <div
        className={cn(
            'flex flex-wrap items-center gap-4 py-4',
            floating &&
                'border-gold-500 sticky bottom-0 z-10 -mx-5 border-t bg-white px-5 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]',
        )}
    >
        {children}
    </div>
);

export const Chip = ({ children, onRemove }: { children: ReactNode; onRemove?: () => void }) => (
    <span className="bg-seafoam inline-flex items-center gap-2 rounded-full py-1 pr-2 pl-3 text-[13px] font-medium">
        {children}
        {onRemove && <RemoveButton onClick={onRemove} />}
    </span>
);

export const Notice = ({ children }: { children: ReactNode }) => (
    <div className="bg-sand-100 text-cocoa-900 rounded px-3.5 py-2.5 text-[13px]">{children}</div>
);
